/**
 * ThemeToggle (task_015)
 *
 * Botão sol/luvio que alterna entre light e dark mode. Usa o `useTheme` hook
 * que delega para `theme.store` — a store cuida de aplicar a classe `.dark`
 * no `<html>` e persistir em localStorage.
 *
 * Renderiza:
 *   • <Sun /> quando o tema atual é 'dark' (clique → vira light)
 *   • <Moon /> quando o tema atual é 'light' (clique → vira dark)
 *
 * Acessibilidade:
 *   • `aria-label` traduzido informando a ação ("Tema claro" / "Tema escuro")
 *   • `aria-pressed` reflete o estado atual
 *   • Variant `ghost` + size `icon` (40×40) — compatível com header da AppShell.
 */
import * as React from 'react'
import { Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/lib/utils'

export interface ThemeToggleProps {
  className?: string
}

export function ThemeToggle({
  className,
}: ThemeToggleProps): React.ReactElement {
  const { t } = useTranslation('common')
  const { theme, toggle } = useTheme()
  const isDark = theme === 'dark'

  // aria-label: "Alternar tema — Tema [claro|escuro]" para leitores de tela.
  // O label da ação descreve o destino do toggle (não o estado atual).
  const targetLabel = isDark ? t('theme.light') : t('theme.dark')
  const ariaLabel = `${t('theme.toggle')} — ${targetLabel}`

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={ariaLabel}
      aria-pressed={isDark}
      title={targetLabel}
      data-testid="theme-toggle"
      data-theme={theme}
      className={cn(className)}
    >
      {isDark ? (
        <Sun className="h-5 w-5" aria-hidden="true" />
      ) : (
        <Moon className="h-5 w-5" aria-hidden="true" />
      )}
    </Button>
  )
}
